/**
 * Songsterr JSON parser - converts Songsterr drum tracks to ScoreV2 format
 */

import type { ScoreV2, ParseOptions } from "./types";
import { GM_TO_LANE, laneFromPitchHeuristic, maskBitForLane } from "./constants";

function durationToTicks(duration: any, ppq: number): number {
  if (!Array.isArray(duration) || duration.length !== 2) return 0;
  const num = Number(duration[0]);
  const den = Number(duration[1]);
  if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return 0;
  return Math.round((ppq * 4 * num) / den);
}

function tempoPositionToTick(
  position: any,
  ppq: number,
  sigNum: number,
  sigDen: number
): number {
  if (Array.isArray(position)) {
    return durationToTicks(position, ppq);
  }

  if (typeof position === "number" && Number.isFinite(position)) {
    const unitTicks = Math.round((ppq * 4) / sigDen);
    if (Number.isInteger(position) && position >= 0 && position <= sigNum) {
      return position * unitTicks;
    }
    return Math.round(position);
  }

  return 0;
}

/**
 * Parse Songsterr drum track JSON to ScoreV2 format
 */
export function parseSongsterrDrumsToScoreV2(
  trackJson: any,
  opts?: ParseOptions
): ScoreV2 {
  const ppqBase = Math.max(120, Math.round(opts?.ppqBase ?? 1920));
  const ppqMultiplier = Math.max(1, Math.min(32, Math.round(opts?.ppqMultiplier ?? 8)));
  const internalPpq = ppqBase * ppqMultiplier;

  const flashMs = Math.max(10, Math.min(400, Math.round(opts?.flashMs ?? 120)));

  const measures = Array.isArray(trackJson?.measures) ? trackJson.measures : [];
  if (!measures.length) {
    return { ppq: internalPpq, tempo_map: [], events: [] };
  }

  // Time signatures per measure
  let sigNum = 4;
  let sigDen = 4;

  const measureStartTick = new Array(measures.length + 1).fill(0);
  const measureSigNum = new Array(measures.length).fill(4);
  const measureSigDen = new Array(measures.length).fill(4);

  for (let i = 0; i < measures.length; i++) {
    const sig = measures[i]?.signature;
    if (
      Array.isArray(sig) &&
      sig.length === 2 &&
      Number.isInteger(sig[0]) &&
      Number.isInteger(sig[1])
    ) {
      sigNum = sig[0];
      sigDen = sig[1];
    }
    measureSigNum[i] = sigNum;
    measureSigDen[i] = sigDen;

    const measureLenTicks = Math.round(internalPpq * 4 * (sigNum / sigDen));
    measureStartTick[i + 1] = measureStartTick[i] + measureLenTicks;
  }

  // Tempo map
  const tempo_map: Array<{ tick: number; bpm: number }> = [];
  const tempoList = trackJson?.automations?.tempo;

  if (Array.isArray(tempoList)) {
    for (const t of tempoList) {
      if (!t || typeof t !== "object") continue;
      const measureIndex = t.measure;
      const bpm = t.bpm;
      if (
        !Number.isInteger(measureIndex) ||
        measureIndex < 0 ||
        measureIndex >= measures.length
      )
        continue;
      if (!(typeof bpm === "number" && Number.isFinite(bpm)) || bpm <= 0)
        continue;

      const baseTick = measureStartTick[measureIndex];
      const posTick = tempoPositionToTick(
        t.position,
        internalPpq,
        measureSigNum[measureIndex],
        measureSigDen[measureIndex]
      );
      tempo_map.push({ tick: baseTick + posTick, bpm: Number(bpm) });
    }

    tempo_map.sort((a, b) => a.tick - b.tick);
    for (let i = tempo_map.length - 2; i >= 0; i--) {
      if (tempo_map[i].tick === tempo_map[i + 1].tick) {
        tempo_map.splice(i, 1);
      }
    }
  }

  const baseBpm =
    tempo_map.length
      ? tempo_map[0].bpm
      : Number(trackJson?.bpm ?? 120) || 120;
  const flashTicks = Math.max(
    1,
    Math.round((flashMs * baseBpm * internalPpq) / 60000)
  );

  const rawEvents: Array<{ t0: number; dt: number; m: number }> = [];

  // Parse measures/voices/beats
  for (let mi = 0; mi < measures.length; mi++) {
    const measure = measures[mi] || {};
    const voices = Array.isArray(measure?.voices) ? measure.voices : [];

    // Rest measure'ları tamamen atla - measureStartTick zaten doğru hesaplanmış
    const measureIsRest = measure.rest === true;
    if (measureIsRest) continue;

    for (const voice of voices) {
      const voiceIsRest = voice?.rest === true;
      if (voiceIsRest) continue;
      
      const beats = Array.isArray(voice?.beats) ? voice.beats : [];
      let tick = measureStartTick[mi];

      for (const beat of beats) {
        const beatTicks = durationToTicks(beat?.duration, internalPpq);
        const dtBeat = beatTicks > 0 ? beatTicks : 1;

        const beatIsRest = beat?.rest === true;
        if (beatIsRest) {
          tick += dtBeat;
          continue;
        }

        const notes = Array.isArray(beat?.notes) ? beat.notes : [];
        let mask = 0;

        for (const n of notes) {
          if (!n || typeof n !== "object") continue;
          if (n.rest === true) continue;

          const pitch = n.fret;
          if (!Number.isInteger(pitch)) continue;

          const lane = GM_TO_LANE[pitch] ?? laneFromPitchHeuristic(pitch);
          mask |= maskBitForLane(lane);
        }

        if (mask !== 0) {
          rawEvents.push({ t0: tick, dt: flashTicks, m: mask });
        }

        tick += dtBeat;
      }
    }
  }

  rawEvents.sort((a, b) => a.t0 - b.t0);

  // Merge events in a small tick window to join same-time hits
  const mergeWindowTicks = Math.max(1, Math.round(internalPpq / 960));
  const events: Array<{ t0: number; dt: number; m: number }> = [];

  for (const e of rawEvents) {
    const last = events.length ? events[events.length - 1] : null;
    if (!last) {
      events.push({ ...e });
      continue;
    }
    if (e.t0 <= last.t0 + mergeWindowTicks) {
      last.m |= e.m;
      last.dt = Math.max(last.dt, e.dt);
    } else {
      events.push({ ...e });
    }
  }

  return { ppq: internalPpq, tempo_map, events };
}

/**
 * Get time signature from track object
 */
export function getTimeSignatureFromTrack(trackObj: any): string {
  const sig = trackObj?.measures?.[0]?.signature;
  if (
    Array.isArray(sig) &&
    sig.length === 2 &&
    Number(sig[0]) &&
    Number(sig[1])
  ) {
    return `${sig[0]}/${sig[1]}`;
  }
  return "4/4";
}

/**
 * Get BPM from track object or score
 */
export function getBpmFromTrack(
  trackObj: any,
  score: ScoreV2 | null,
  fallback = 120
): number {
  if (score?.tempo_map?.length) {
    return Math.round(score.tempo_map[0].bpm);
  }
  const b = Number(trackObj?.bpm);
  if (Number.isFinite(b) && b > 0) {
    return Math.round(b);
  }
  return fallback;
}
